import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useEffect, useRef, useState } from 'react'

import { MinusIcon, PlusIcon } from '../../../icons'
import { InputProps } from '.'
import BaseInput from './base-input'
import { getBaseInputProps } from './input-props'
import InputWrapper from './input-wrapper'

export interface NumberInputProps {
  isControllable?: boolean
  allowNegative?: boolean
  displayIncrementDecrementButtons?: boolean
  decimalPlaces?: number
}

interface BaseInputProps extends Omit<InputProps, 'type'>, NumberInputProps {}

// check if the value is a number and not null or undefined or empty string
function isNumber(value: number | string | null | undefined) {
  return value !== undefined && value !== null && value !== ''
}

const NumberInput: React.FC<BaseInputProps> = ({
  label,
  active,
  icon,
  suffix,
  iconStyle,
  errorMessage,
  isControllable = false,
  allowNegative = true,
  displayIncrementDecrementButtons = false,
  decimalPlaces,
  wrapperClassName,
  ...props
}) => {
  const [value, setValue] = useState<number | string>('')
  const ref = useRef<HTMLInputElement>(null)
  const baseInputProps = getBaseInputProps(props)
  // checking for nullish since value can be 0 which is falsy
  const isActive = active || isNumber(props.value)

  useEffect(() => {
    // If the component is not controllable, we manage the value internally and ignore external changes
    if (!isNumber(props.value)) {
      setValue('')
      return
    }

    setValue(Number(props.value))
  }, [props.value])

  const validateDecimalPlaces = (inputValue: string): boolean => {
    if (decimalPlaces === undefined) return true

    const decimalIndex = inputValue.indexOf('.')
    if (decimalIndex === -1) return true

    const decimals = inputValue.substring(decimalIndex + 1)
    return decimals.length <= decimalPlaces
  }

  const handleIncrement = () => {
    if (!isControllable) return

    setValue((prevValue) => {
      const newValue = (Number(prevValue) || 0) + 1
      if (props.onChange)
        props.onChange({
          target: { value: newValue }
        } as unknown as React.ChangeEvent<HTMLInputElement>)
      return newValue
    })
  }

  const handleDecrement = () => {
    if (!isControllable) return

    setValue((prevValue) => {
      const newValue = (Number(prevValue) || 0) - 1
      if (!allowNegative && newValue < 0) return 0
      if (props.onChange)
        props.onChange({
          target: { value: newValue }
        } as unknown as React.ChangeEvent<HTMLInputElement>)
      return newValue
    })
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // React does not resync a number input whose text equals the value numerically ("08" == 8),
    // so typing after a "0" would leave the zero in front: drop leading zeros before a digit
    const strippedValue = e.target.value.replace(/^(-?)0+(?=\d)/, '$1')
    if (strippedValue !== e.target.value) e.target.value = strippedValue
    const newValue = e.target.value
    if (!allowNegative && newValue.includes('-')) return
    if (!validateDecimalPlaces(newValue)) return
    setValue(newValue)
    if (props.onChange) props.onChange(e)
  }

  return (
    <InputWrapper
      label={label}
      active={isActive}
      icon={icon}
      iconStyle={iconStyle}
      suffix={suffix}
      errorMessage={errorMessage}
      className="pr-2"
      wrapperClassName={wrapperClassName}
      {...props}
    >
      {(_, setIsActive) => (
        <div
          className={cn(
            'flex items-center gap-2 h-full cursor-text',
            props.disabled && 'opacity-50 cursor-not-allowed'
          )}
          onClick={() => {
            const inputElement = ref.current

            // If input is not focused, focus it
            if (inputElement && document.activeElement !== inputElement) {
              inputElement.focus()
              // For number inputs, we can't use setSelectionRange
              // Instead, we'll use a workaround to move the cursor to the end
              const value = inputElement.value
              inputElement.value = ''
              inputElement.value = value
            }
          }}
        >
          <BaseInput
            {...baseInputProps}
            ref={ref}
            type="number"
            className="w-full"
            aria-label={label}
            setIsActive={setIsActive}
            value={value}
            onChange={handleChange}
            step={
              decimalPlaces !== undefined
                ? Math.pow(10, -decimalPlaces).toFixed(decimalPlaces)
                : undefined
            }
            onKeyDown={(e) => {
              if (e.key === 'e' || (e.key === '-' && !allowNegative)) e.preventDefault()
            }}
          />
          {displayIncrementDecrementButtons && (
            <div
              className={cn(
                'flex items-center z-[1]',
                props.disabled && 'pointer-events-none opacity-50',
                !isControllable && 'opacity-0 pointer-events-none'
              )}
            >
              <button
                type="button"
                className={cn(
                  'bg-white-active px-1 h-6 rounded-l-sm text-grey-light-active flex items-center justify-center transition-colors',
                  {
                    'hover:bg-white-hover hover:text-grey-lighter':
                      !props.disabled && !props.readOnly,
                    'cursor-not-allowed': props.disabled || props.readOnly
                  }
                )}
                onClick={() => {
                  if (props.readOnly || props.disabled) return
                  handleDecrement()
                  setIsActive(true)
                }}
              >
                <MinusIcon color={tailwindColors.grey.light.active} />
              </button>
              <button
                type="button"
                className={cn(
                  'bg-white-active px-1 h-6 rounded-r-sm text-grey-light-active flex items-center justify-center transition-colors',
                  {
                    'hover:bg-white-hover hover:text-grey-lighter':
                      !props.disabled && !props.readOnly,
                    'cursor-not-allowed': props.disabled || props.readOnly
                  }
                )}
                onClick={() => {
                  if (props.readOnly || props.disabled) return
                  handleIncrement()
                  setIsActive(true)
                }}
              >
                <PlusIcon color={tailwindColors.grey.light.active} />
              </button>
            </div>
          )}
        </div>
      )}
    </InputWrapper>
  )
}

export default NumberInput
